import type { SourceSystemResource } from '../types/source-system';

/** Pipeline health colour based on lastRunStatus from SourceSystemResource. */
function healthClass(status: string | null): string {
  if (!status) return 'pl-selector__dot--unknown';
  switch (status) {
    case 'completed':
      return 'pl-selector__dot--ok';
    case 'partial':
      return 'pl-selector__dot--warn';
    case 'failed':
      return 'pl-selector__dot--fail';
    case 'running':
    case 'pending':
      return 'pl-selector__dot--active';
    case 'cancelled':
    case 'skipped':
      return 'pl-selector__dot--muted';
    default:
      return 'pl-selector__dot--unknown';
  }
}

interface PipelineSelectorProps {
  resources: SourceSystemResource[];
  selectedResourceUuid: string | null;
  onSelect: (resourceUuid: string) => void;
}

/**
 * Left-hand pipeline list sidebar.
 *
 * Splits resources into Build and Release groups based on resourceType
 * heuristic: resources with "release" in providerResourceId are release
 * pipelines; all others are build pipelines.
 *
 * Note: SourceSystemResource does not carry pipelineCategory directly,
 * so we infer it from the resource name / ID until the API is extended.
 * In practice the build resources and release resources are separate
 * project_integration_resource rows (resource_type='list_pipelines' vs
 * 'list_release_pipelines'), BUT the panel state doesn't expose resourceType.
 * We group them into a single flat list for now - the detail pane shows the
 * category badge. A future API change can add category to SourceSystemResource.
 */
export function PipelineSelector({
  resources,
  selectedResourceUuid,
  onSelect,
}: PipelineSelectorProps) {
  if (resources.length === 0) {
    return (
      <div className="pl-selector__empty">
        No pipelines configured. Open Settings to add pipelines.
      </div>
    );
  }

  return (
    <ul className="pl-selector__list" role="listbox" aria-label="Pipelines">
      {resources.map((r) => {
        const isSelected = r.resourceUuid === selectedResourceUuid;
        return (
          <li key={r.resourceUuid} role="option" aria-selected={isSelected}>
            <button
              type="button"
              className={`pl-selector__row${isSelected ? ' pl-selector__row--selected' : ''}`}
              onClick={() => onSelect(r.resourceUuid)}
            >
              <span
                className={`pl-selector__dot ${healthClass(r.lastRunStatus)}`}
                aria-hidden="true"
              />
              <span className="pl-selector__name">{r.providerResourceName}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
